'use client';

import type { ReactNode } from 'react';

import classnames from 'classnames';
import {
  IoCheckmarkCircle,
  IoCheckmarkCircleOutline,
  IoWarningOutline,
} from 'react-icons/io5';

import type { StepVariant, StepperProps } from './Stepper.types';
import { StepperLabel } from './layout/StepperLabel';

import styles from './Stepper.module.scss';

const getIconFromVariant = ({
  icon,
  variant,
}: {
  icon: ReactNode;
  variant: StepVariant;
}) => {
  if (icon) {
    return icon;
  }
  return {
    active: <IoCheckmarkCircleOutline />,
    completed: <IoCheckmarkCircle />,
    inactive: <IoCheckmarkCircleOutline />,
    disabled: <IoCheckmarkCircleOutline />,
    error: <IoWarningOutline />,
  }[variant];
};

function Stepper({
  style,
  className,
  showIcons,
  steps,
  activeStep,
  onClickStep,
  testId = 'ui-stepper',
}: StepperProps) {
  return (
    <div
      style={style}
      className={classnames(styles['stepper'], className)}
      data-testid={testId}
    >
      {steps.map(({ icon, label, variant }, index) => (
        <div
          key={label}
          className={classnames(
            styles['stepper-label'],
            index === steps.length - 1 && styles[`stepper-label--last`],
          )}
        >
          <StepperLabel
            aria-current={activeStep === index}
            disabled={variant === 'disabled'}
            variant={activeStep === index ? 'active' : variant || 'inactive'}
            icon={
              showIcons
                ? getIconFromVariant({ icon, variant: variant || 'inactive' })
                : null
            }
            label={label}
            onClick={() => onClickStep?.(index)}
          />
          {index < steps.length - 1 && (
            <div className={classnames(styles['line'])} />
          )}
        </div>
      ))}
    </div>
  );
}

export { Stepper };
